import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { I18nProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./lib/auth";
import { EmployeeAuthProvider, RequireEmployee } from "./lib/employeeAuth";
import CompanyAuthenticatedShell from "./components/CompanyAuthenticatedShell";

// Phase 16 Stage 6C.3 — Public document-request SPA. Deliberately
// mounted OUTSIDE the authenticated provider tree below so visiting
// ``/dr/:rawToken`` never activates ``AuthProvider``,
// ``EmployeeAuthProvider``, ``AccountantAuthProvider``, or
// ``CompanyBrandProvider``. See ``/public/PublicDocumentRequestApp.jsx``.
const PublicDocumentRequestApp = lazy(() => import("./public/PublicDocumentRequestApp"));

// Route-level code splitting (Fix D). Each page becomes its own bundle chunk,
// loaded on demand. Wrapped in a <Suspense> below with a neutral fallback so
// pages stay visually identical once mounted — only the *load timing* changes.
const Launch = lazy(() => import("./pages/Launch"));
const Welcome = lazy(() => import("./pages/Welcome"));
const ChooseWorkspace = lazy(() => import("./pages/ChooseWorkspace"));
const AccountantInvitation = lazy(() => import("./pages/AccountantInvitation"));
const SponsorshipActivation = lazy(() => import("./pages/SponsorshipActivation"));
const Landing = lazy(() => import("./pages/Landing"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ThisMonth = lazy(() => import("./pages/ThisMonth"));
const Months = lazy(() => import("./pages/Months"));
const MonthDetail = lazy(() => import("./pages/MonthDetail"));
const SendDetail = lazy(() => import("./pages/SendDetail"));
const Packages = lazy(() => import("./pages/Packages"));
const PackageDetail = lazy(() => import("./pages/PackageDetail"));
const AccountantPackage = lazy(() => import("./pages/AccountantPackage"));
const Capture = lazy(() => import("./pages/Capture"));
const CaptureEmail = lazy(() => import("./pages/CaptureEmail"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Detail = lazy(() => import("./pages/Detail"));
const DocumentViewer = lazy(() => import("./pages/DocumentViewer"));
const MissingReceipt = lazy(() => import("./pages/MissingReceipt"));
const Settings = lazy(() => import("./pages/Settings"));
const InstallGuide = lazy(() => import("./pages/InstallGuide"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AdminOverview = lazy(() => import("./pages/AdminOverview"));
const AdminCompanyDetail = lazy(() => import("./pages/AdminCompanyDetail"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
// Phase 16 Stage 5A — Firm Co-branding (internal admin only).
const AdminCoBranding = lazy(() => import("./pages/AdminCoBranding"));
// Phase 16 Stage 5E.1 — Internal admin Ordly platform theme.
const AdminPlatformAppearance = lazy(() => import("./pages/AdminPlatformAppearance"));
const Accountant = lazy(() => import("./pages/Accountant"));

// Phase Employee Access — Stage 2 (Aug 2026). Owner-side employee
// management screens, gated behind RequireFullAccess.
const OwnerEmployees = lazy(() => import("./pages/OwnerEmployees"));
const OwnerEmployeeDetail = lazy(() => import("./pages/OwnerEmployeeDetail"));

// Phase Employee Access — Stage 1B (Aug 2026). Cookie-authenticated
// employee shell. Disjoint from owner AuthProvider; uses its own
// EmployeeAuthProvider scoped to the /e/* subtree.
const EmployeeRedeem = lazy(() => import("./pages/employee/EmployeeRedeem"));
const EmployeeHome = lazy(() => import("./pages/employee/EmployeeHome"));
const EmployeeCapture = lazy(() => import("./pages/employee/EmployeeCapture"));
const EmployeeDocuments = lazy(() => import("./pages/employee/EmployeeDocuments"));
const EmployeeDocumentDetail = lazy(() => import("./pages/employee/EmployeeDocumentDetail"));
const EmployeeRevoked = lazy(() => import("./pages/employee/EmployeeRevoked"));

// Accountant Workspace — Phase 1 MVP (Stage 0 + 1A). Mounted only when
// REACT_APP_ACCOUNTANT_WORKSPACE_ENABLED === "true" at build time. The
// shell owns its own nested <Routes> for the /firm/* subtree, its own
// AccountantAuthProvider, and never touches the Company AuthProvider.
const AccountantWorkspaceShell = lazy(() => import("./pages/firm/AccountantWorkspaceShell"));

// Neutral, layout-agnostic fallback while a route chunk is in-flight. No
// branding, no copy — just an empty placeholder so we never flash partial
// content from a previous page. Pages render their own design as soon as
// their chunk resolves (typically <100 ms on warm caches).
function RouteFallback() {
    return <div data-testid="route-suspense-fallback" aria-hidden="true" />;
}

function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/welcome" replace />;
    return children;
}

// Block capture-only users from full-workspace screens (dashboards, months, packages).
function RequireFullAccess({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/welcome" replace />;
    if (user.role === "capture_only") return <Navigate to="/capture" replace />;
    return children;
}

// Platform-admin only — wraps internal /admin routes.
// Backend re-checks the same flag on every /api/admin/* request, so this is
// purely a UX redirect; never a security boundary.
function RequirePlatformAdmin({ children }) {
    const { user, isPlatformAdmin, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!isPlatformAdmin) return <Navigate to="/" replace />;
    return children;
}

// Employee Shell — provides EmployeeAuthProvider + RequireEmployee guard for
// every /e/* page that is NOT /e/:token or /e/revoked. The shell never reads
// from the owner AuthProvider; the two auth surfaces stay disjoint.
function EmployeeShell({ children }) {
    return (
        <EmployeeAuthProvider>
            <RequireEmployee>{children}</RequireEmployee>
        </EmployeeAuthProvider>
    );
}

function App() {
    useEffect(() => {
        // PWA service worker registration (best-effort)
        if ("serviceWorker" in navigator) {
            window.addEventListener("load", () => {
                navigator.serviceWorker.register("/sw.js").catch(() => {});
            });
        }
    }, []);

    // Stage 6C.3 — hard isolation guard. If the user lands on
    // ``/dr/:rawToken`` we render an ENTIRELY separate app subtree
    // that mounts NO authenticated provider, NO Company/Accountant/
    // Employee auth context, and never touches localStorage auth
    // tokens. Every authenticated provider below stays unmounted.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/dr/")) {
        return (
            <div className="App">
                <Suspense fallback={<RouteFallback />}>
                    <PublicDocumentRequestApp />
                </Suspense>
            </div>
        );
    }

    return (
        <div className="App">
            <I18nProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <Suspense fallback={<RouteFallback />}>
                            <Routes>
                                <Route path="/" element={<Launch />} />
                                <Route path="/launch" element={<Launch />} />
                                <Route path="/choose-workspace" element={<ChooseWorkspace />} />
                                <Route path="/accountant-invitations/:token" element={<AccountantInvitation />} />
                                <Route path="/sponsorship-invitations/:token" element={<SponsorshipActivation />} />
                                <Route path="/welcome" element={<Welcome />} />
                                <Route path="/landing" element={<Landing />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/verify-email" element={<VerifyEmail />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/privacy" element={<Privacy />} />
                                {/* Phase 16 Stage 5C — Company-authenticated layout route.
                                    Owns CompanyBrandProvider for live co-branding. NEVER
                                    wraps pre-auth, employee, accountant, or admin routes. */}
                                <Route element={<CompanyAuthenticatedShell />}>
                                <Route
                                    path="/onboarding"
                                    element={
                                        <RequireAuth>
                                            <Onboarding />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/this-month"
                                    element={
                                        <RequireFullAccess>
                                            <ThisMonth />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/capture"
                                    element={
                                        <RequireAuth>
                                            <Capture />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/capture/email"
                                    element={
                                        <RequireAuth>
                                            <CaptureEmail />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/inbox"
                                    element={
                                        <RequireAuth>
                                            <Inbox />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/inbox/:id"
                                    element={
                                        <RequireAuth>
                                            <Detail />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/documents/:id/viewer"
                                    element={
                                        <RequireAuth>
                                            <DocumentViewer />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/months"
                                    element={
                                        <RequireFullAccess>
                                            <Months />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/months/:ym"
                                    element={
                                        <RequireFullAccess>
                                            <MonthDetail />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/months/:ym/sends/:packageId"
                                    element={
                                        <RequireFullAccess>
                                            <SendDetail />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/packages"
                                    element={
                                        <RequireFullAccess>
                                            <Packages />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/accountant"
                                    element={
                                        <RequireFullAccess>
                                            <Accountant />
                                        </RequireFullAccess>
                                    }
                                />

                                <Route
                                    path="/packages/:id"
                                    element={
                                        <RequireFullAccess>
                                            <PackageDetail />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/missing-receipt"
                                    element={
                                        <RequireFullAccess>
                                            <MissingReceipt />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <RequireAuth>
                                            <Settings />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/install"
                                    element={
                                        <RequireAuth>
                                            <InstallGuide />
                                        </RequireAuth>
                                    }
                                />
                                <Route
                                    path="/employees"
                                    element={
                                        <RequireFullAccess>
                                            <OwnerEmployees />
                                        </RequireFullAccess>
                                    }
                                />
                                <Route
                                    path="/employees/:id"
                                    element={
                                        <RequireFullAccess>
                                            <OwnerEmployeeDetail />
                                        </RequireFullAccess>
                                    }
                                />
                                </Route>
                                {/* /Company-authenticated layout route */}
                                <Route path="/p/:token" element={<AccountantPackage />} />
                                <Route
                                    path="/admin"
                                    element={
                                        <RequirePlatformAdmin>
                                            <AdminOverview />
                                        </RequirePlatformAdmin>
                                    }
                                />
                                <Route
                                    path="/admin/companies/:id"
                                    element={
                                        <RequirePlatformAdmin>
                                            <AdminCompanyDetail />
                                        </RequirePlatformAdmin>
                                    }
                                />
                                <Route path="/admin/users"
                                    element={
                                        <RequirePlatformAdmin>
                                            <AdminUsers />
                                        </RequirePlatformAdmin>
                                    }
                                />
                                {/* Phase 16 Stage 5A — Firm Co-branding
                                    (internal admin only; backend re-checks
                                    ORDLY_FIRM_BRANDING_ENABLED on every call). */}
                                <Route path="/admin/co-branding"
                                    element={
                                        <RequirePlatformAdmin>
                                            <AdminCoBranding />
                                        </RequirePlatformAdmin>
                                    }
                                />
                                {/* Phase 16 Stage 5E.1 — Internal admin
                                    Ordly platform theme (read-mostly:
                                    preview presets + activate + restore
                                    Classic). Backend re-checks
                                    require_platform_admin on every
                                    /api/admin/ordly-theme/* request. */}
                                <Route path="/admin/platform-appearance"
                                    element={
                                        <RequirePlatformAdmin>
                                            <AdminPlatformAppearance />
                                        </RequirePlatformAdmin>
                                    }
                                />
                                {/* Employee Access — Stage 2 (Aug 2026).
                                    Owner-side management routes (/employees,
                                    /employees/:id) live inside the
                                    CompanyAuthenticatedShell layout above. */}
                                {/* Employee Access — Stage 1B (Aug 2026).
                                    Disjoint cookie-authenticated subtree. Each
                                    /e/* page mounts inside EmployeeAuthProvider
                                    via the EmployeeShell wrapper below. The
                                    raw share-link route /e/:token redeems the
                                    cookie and strips the token from the URL
                                    before any further request runs. */}
                                <Route path="/e" element={<EmployeeShell><EmployeeHome /></EmployeeShell>} />
                                <Route path="/e/capture" element={<EmployeeShell><EmployeeCapture /></EmployeeShell>} />
                                <Route path="/e/documents" element={<EmployeeShell><EmployeeDocuments /></EmployeeShell>} />
                                <Route path="/e/documents/:id" element={<EmployeeShell><EmployeeDocumentDetail /></EmployeeShell>} />
                                <Route path="/e/revoked" element={<EmployeeAuthProvider><EmployeeRevoked /></EmployeeAuthProvider>} />
                                <Route path="/e/:token" element={<EmployeeAuthProvider><EmployeeRedeem /></EmployeeAuthProvider>} />
                                {process.env.REACT_APP_ACCOUNTANT_WORKSPACE_ENABLED === "true" && (
                                    <Route path="/firm/*" element={<AccountantWorkspaceShell />} />
                                )}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Suspense>
                    </BrowserRouter>
                </AuthProvider>
            </I18nProvider>
        </div>
    );
}

export default App;
